import { type JSX, type ReactNode, useId, useMemo } from 'react'
import {
  type Control,
  type DeepPartial,
  type FieldError,
  type FieldErrors,
  type FieldNamesMarkedBoolean,
  type FieldPath,
  type FieldPathValue,
  type FieldValues,
  type Noop,
  type RefCallBack,
  useController,
} from 'react-hook-form'
import { toast } from 'sonner'
import { useDidChange } from '#src/hooks/useDidChange.ts'
import { FormFieldContext } from '#src/hooks/useFormField.ts'

export interface FormFieldProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> {
  control: Control<TFieldValues>
  name: TName
  render: ({
    field,
    fieldState,
    formState,
  }: {
    field: FieldWithRef<FieldPathValue<TFieldValues, TName>>
    fieldState: FieldState
    formState: FormState<TFieldValues>
  }) => ReactNode
  disabled?: boolean
}

// Note: We typically pass all of Field's properties to the same component by using <Component {...field} />. In addition to all of the properties below, ref should also be passed. If you need to pass them more granularly, see the descriptions below to understand each property's responsibility. You can spread { value, onChange, ...fieldRest } to handle value and onChange separately and then use <Component {...fieldRest} /> to pass the rest of the properties. Note that there should never be a reason that ref, id, and aria-describedby are not passed to the same component.
export interface Field<TValue> {
  value: TValue
  onChange: (value: TValue) => any
  onBlur: Noop // Pass onBlur to whichever component(s) indicate that the field has been blurred. It's used to report to the form that the field has been interacted with (i.e. isTouched).
  id: string // Pass id to the component that you'd like FormFieldLabel to reference. This id is used for `htmlFor` on FormFieldLabel to ensure that clicking on the label focuses the component you've passed id to.
  'aria-describedby': string // Pass aria-describedby to the component that you'd like FormFieldDescription to refer to.
  'aria-invalid': boolean // Pass aria-invalid to the component(s) that FormField can mark as invalid.
  disabled?: boolean // Pass disabled to the component(s) that FormField can disable.
}

interface FieldWithRef<TValue> extends Field<TValue> {
  ref: RefCallBack
}

export interface FieldState {
  invalid: boolean
  isTouched: boolean
  isDirty: boolean
  isValidating: boolean
  error?: FieldError
}

export interface FormState<TFieldValues extends FieldValues = FieldValues> {
  isDirty: boolean
  isLoading: boolean
  isSubmitted: boolean
  isSubmitSuccessful: boolean
  isSubmitting: boolean
  isValidating: boolean
  isValid: boolean
  disabled: boolean
  submitCount: number
  defaultValues?: undefined | Readonly<DeepPartial<TFieldValues>>
  dirtyFields: Partial<Readonly<FieldNamesMarkedBoolean<TFieldValues>>>
  touchedFields: Partial<Readonly<FieldNamesMarkedBoolean<TFieldValues>>>
  validatingFields: Partial<Readonly<FieldNamesMarkedBoolean<TFieldValues>>>
  errors: FieldErrors<TFieldValues>
}

export function FormField<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  control,
  name,
  render,
  disabled,
}: FormFieldProps<TFieldValues, TName>): JSX.Element {
  const id = useId()
  const formDescriptionId = `${id}-form-item-description`
  const formErrorMessageId = `${id}-form-item-message`

  const controller = useController({ control, name, disabled })

  const renderProps = useMemo(
    () => ({
      field: {
        ref: controller.field.ref,
        value: controller.field.value,
        onChange: controller.field.onChange,
        id,
        onBlur: controller.field.onBlur,
        'aria-describedby': !controller.fieldState.error
          ? formDescriptionId
          : `${formDescriptionId} ${formErrorMessageId}`,
        'aria-invalid': !!controller.fieldState.error,
        disabled: controller.field.disabled,
      },
      fieldState: controller.fieldState,
      formState: controller.formState,
    }),
    [
      id,
      controller.field,
      formDescriptionId,
      formErrorMessageId,
      controller.fieldState,
      controller.formState,
    ]
  )

  const formFieldContext = useMemo(
    () => ({
      ...controller.fieldState,
      name,
      formItemId: id,
      formDescriptionId,
      formErrorMessageId,
    }),
    [name, id, formDescriptionId, formErrorMessageId, controller.fieldState]
  )

  const validationError = controller.fieldState.error?.message
  const didErrorChange = useDidChange(validationError)
  const didSubmitCountChange = useDidChange(controller.formState.submitCount)
  if (!!validationError && (didErrorChange || didSubmitCountChange)) {
    toast.error(validationError, {
      id: validationError,
    })
  }

  return (
    <FormFieldContext.Provider value={formFieldContext}>
      {render(renderProps)}
    </FormFieldContext.Provider>
  )
}
